import React, { useContext } from 'react';

import {
	ApButtonSecondary,
	ApButtonOrange,
	ApLoaderDefault,
	ApLoaderText,
	ApRadio,
	ApCheckbox,
	ApIcon,
	colors,
	ApButtonGroup,
} from '@alixpartners/ui-components';
import {
	StyledModal,
	Content,
	RightSection,
	MainSection,
	LoadingContainer,
	OrangeText,
	FormGroup,
	WarningMessage,
	Text,
	Label,
	HorizontalGroup,
	RadioLabel,
	RadioLabelSmall,
	RadioGroup,
	PreveiwContentWrapper,
	VendorsListCell,
	VendorsListHeader,
	VendorsListRow,
	VendorsList,
	LoadingFooter,
	LoadingWrapper,
	ActionableContent,
	CategoryWrapper,
	PlaceholderText,
} from './CategorisationDialog.styles';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import {
	getProjectTaxonomies,
	getTransactionsCount,
	getClassificationOverrideRules,
	createUpdateClassificationOverrideRule,
	getHarmonizedData,
	getProjectTaxonomiesIndirectClient,
} from 'utils/apiRequests';
import { SimpleTree } from 'components/SimpleTree';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { SearchInput } from 'components/SearchInput';
import { SPEND_TYPE } from 'Data/constants';
import { getTaxonomyTree, getTaxonomyOptions } from './utils';
import { COLUMNS } from '../tableMetadata';

const EDIT_STEP = 1;
const OVERRIDE_STEP = 2;

const SPEND_TYPE_VALUES = {
	INDIRECT: 'INDIRECT',
	DIRECT: 'DIRECT',
};

export class CategorisationDialog extends React.Component {
	state = {
		selectedItem: null,
		treeData: null,
		isLoading: true,
		treeQuery: null,
		isDirect: false,
		isAllTransactions: false,
		shouldCreateRule: false,
		directTaxonomies: null,
		indirectTaxonomies: null,
		indirectClientTaxonomies: null,
		formStep: EDIT_STEP,
		suppressSpendType: false,
		transactionsCount: 0,
		contextTransactionsData: null,
		overrideRulesMap: {},
		harmonizedVendors: [],
		isApplying: false,
		...this.getStateFromProps(),
	};

	async componentDidMount() {
		await this.fetchData();
		this.updateTransactionsCount();
	}

	componentDidUpdate(_prevProps, prevState) {
		const { isDirect, isAllTransactions } = this.state;

		if (isDirect !== prevState.isDirect) {
			this.updateTreeData();
			this.updateTransactionsCount();

			if (isDirect) {
				this.setState({ shouldCreateRule: false });
			}
		}

		if (isAllTransactions !== prevState.isAllTransactions) {
			this.fetchTransactionsCount();
		}
	}

	getSpendTypeTransactions = () => {
		const { selectedRowNodes } = this.props;
		const { isDirect } = this.state;

		if (!selectedRowNodes) {
			return [];
		}

		return selectedRowNodes.filter(
			item =>
				item.data[COLUMNS.SPEND_TYPE] ===
				(isDirect ? SPEND_TYPE_VALUES.DIRECT : SPEND_TYPE_VALUES.INDIRECT)
		);
	};

	updateTransactionsCount = () => {
		const { selectedRowNodes } = this.props;
		const { contextTransactionsData, isDirect } = this.state;

		if (selectedRowNodes) {
			this.setState({
				transactionsCount: this.getSpendTypeTransactions().length,
			});

			return;
		}

		if (!contextTransactionsData) {
			this.fetchTransactionsCount();

			return;
		}

		this.setState({
			transactionsCount: isDirect
				? contextTransactionsData.directSpendsCount
				: contextTransactionsData.indirectSpendsCount,
		});
	};

	fetchTransactionsCount = () => {
		const { contextParams = {}, allTransactionsParams = {} } = this.props;
		const { isDirect, isAllTransactions } = this.state;
		const params = isAllTransactions ? allTransactionsParams : contextParams;

		getTransactionsCount(params)
			.then(contextTransactionsData => {
				this.setState({
					contextTransactionsData,
					transactionsCount: isDirect
						? contextTransactionsData.directSpendsCount
						: contextTransactionsData.indirectSpendsCount,
				});
			})
			.catch(() => {
				this.setState({
					transactionsCount: 0,
				});
			});
	};

	getStateFromProps() {
		const { editingRowNode, selectedRowNodes, contextParams } = this.props;

		if (!selectedRowNodes) {
			const spendTypeIndex = contextParams.filters.findIndex(
				item => item === 'spendType'
			);
			return {
				isDirect:
					spendTypeIndex !== -1
						? contextParams.filterValues[spendTypeIndex] === 0
						: false,
			};
		}

		const isDirect =
			editingRowNode.data[COLUMNS.SPEND_TYPE] === SPEND_TYPE_VALUES.DIRECT;
		const suppressSpendType =
			selectedRowNodes.reduce((acc, item) => {
				return acc.includes(item.data[COLUMNS.SPEND_TYPE])
					? acc
					: [...acc, item.data[COLUMNS.SPEND_TYPE]];
			}, []).length === 1;
		const selectedItem = {
			name: editingRowNode.data[COLUMNS.AP_L4Category],
			value: editingRowNode.data[COLUMNS.AP_L4Category],
			id: `${editingRowNode.data[COLUMNS.AP_L1Category]}${
				editingRowNode.data[COLUMNS.AP_L2Category]
			}${editingRowNode.data[COLUMNS.AP_L3Category]}${
				editingRowNode.data[COLUMNS.AP_L4Category]
			}${!isDirect ? editingRowNode.data[COLUMNS.AP_L4Code] : ''}`,
			data: {
				project_L4Category: editingRowNode.data[COLUMNS.AP_L4Category], // Added for handleCreateOverrideRules
				aP_L4Code: editingRowNode.data[COLUMNS.AP_L4Code], // Added for handleCreateOverrideRules
			},
		};

		return {
			suppressSpendType,
			selectedItem,
			isDirect,
			treeQuery: selectedItem ? selectedItem.id : null,
		};
	}

	async fetchData() {
		const { selectedRowNodes } = this.props;
		await this.fetchTaxonomies();
		await this.fetchOverrideRules();

		if (!selectedRowNodes) {
			await this.fetchHarmonizedVendors();
		}

		this.setState({ isLoading: false });
	}

	fetchTaxonomies = async () => {
		const directTaxonomies = await getProjectTaxonomies(SPEND_TYPE.DIRECT);
		const indirectTaxonomies = await getProjectTaxonomies(SPEND_TYPE.INDIRECT);
		const indirectClientTaxonomies = await getProjectTaxonomiesIndirectClient();

		this.setState(
			{
				directTaxonomies,
				indirectTaxonomies,
				indirectClientTaxonomies,
			},
			this.updateTreeData
		);
	};

	fetchOverrideRules = async () => {
		const overrideRulesData = await getClassificationOverrideRules();

		this.setState({
			overrideRulesMap: overrideRulesData
				.filter(overrideRule => !overrideRule.isDeleted)
				.reduce((acc, item) => {
					return { ...acc, [item.harmonizedVendorId]: item.id };
				}, {}),
		});
	};

	fetchHarmonizedVendors = async () => {
		const harmonizedVendors = await getHarmonizedData();

		this.setState({
			harmonizedVendors,
		});
	};

	handleTreeDataChange = treeData => {
		this.setState({ treeData });
	};

	handleTreeItemClick = node => {
		const { selectedItem } = this.state;
		const newSelection =
			selectedItem && selectedItem.id === node.id ? null : node;

		this.setState({ selectedItem: newSelection, treeQuery: null });
	};

	handleRadioChange = ({ target: { name, value } }) => {
		this.setState({ [name]: !!Number(value) });
	};

	handleSpendTypeChange = ({ target: { value } }) => {
		this.setState({ isDirect: !!Number(value), selectedItem: null });
	};

	handleRuleChange = ({ target: { checked } }) => {
		this.setState({ shouldCreateRule: checked });
	};

	selectSearchItem = selectedItem => {
		this.setState({
			selectedItem,
			treeQuery: selectedItem ? selectedItem.id : null,
		});
	};

	updateTreeData = () => {
		const {
			isDirect,
			directTaxonomies,
			indirectTaxonomies,
			indirectClientTaxonomies,
		} = this.state;

		this.handleTreeDataChange(
			getTaxonomyTree(
				this.props.queryParams?.indirectTaxonomyType === 2 && isDirect
					? indirectClientTaxonomies
					: isDirect
						? directTaxonomies
						: indirectTaxonomies
			)
		);
	};

	getSearchOptions = () => {
		const {
			isDirect,
			directTaxonomies,
			indirectTaxonomies,
			// indirectClientTaxonomies,
		} = this.state;

		return getTaxonomyOptions(
			this.props.queryParams?.indirectTaxonomyType === 2 && !isDirect
				? []
				: isDirect
					? directTaxonomies
					: indirectTaxonomies
		);
	};

	handleApply = async () => {
		const { shouldCreateRule, formStep } = this.state;
		const { selectedRowNodes, onClose } = this.props;

		if (formStep === EDIT_STEP && shouldCreateRule) {
			this.setState({ formStep: OVERRIDE_STEP });

			return;
		}

		this.setState({ isApplying: true });

		try {
			if (selectedRowNodes) {
				await this.handleTransactionsSubmit();
			} else {
				await this.handleSubmit();
			}

			this.handleCreateOverrideRules();
			this.setState({ isApplying: false });

			onClose();
		} catch {
			this.setState({ isApplying: false });
		}
	};

	handleCreateOverrideRules = async () => {
		const {
			selectedItem,
			shouldCreateRule,
			isDirect,
			overrideRulesMap,
		} = this.state;

		if (!shouldCreateRule || isDirect) {
			return;
		}

		const categoryData = {
			AP_L4Category: selectedItem.data.project_L4Category,
			AP_L4Code: selectedItem.data.aP_L4Code,
		};
		const overrideRules = this.getVendors().map(
			({ harmonizedVendorId, harmonizedVendorName }) => ({
				harmonizedVendorId,
				harmonizedVendorName,
				...categoryData,
			})
		);

		let hasCreateRequests = false;

		for (let i = 0; i < overrideRules.length; i += 1) {
			let overrideRule = overrideRules[i];

			if (overrideRulesMap[overrideRule.harmonizedVendorId]) {
				overrideRule = {
					...overrideRule,
					id: overrideRulesMap[overrideRule.harmonizedVendorId],
				};
			} else {
				hasCreateRequests = true;
			}

			await createUpdateClassificationOverrideRule(overrideRule);
		}

		if (hasCreateRequests) {
			this.fetchOverrideRules();
		}
	};

	handleBackToEdit = () => {
		this.setState({ formStep: EDIT_STEP });
	};

	handleSubmit = () => {
		const { selectedItem, isDirect, isAllTransactions } = this.state;
		const { onSave } = this.props;

		if (!selectedItem) {
			return;
		}

		const saveData = {
			taxonomy: selectedItem.data,
			isDirect,
			isAllTransactions,
		};

		return onSave(saveData);
	};

	handleTransactionsSubmit = () => {
		const { shouldCreateRule, selectedItem } = this.state;
		const { onSave, selectedRowNodes } = this.props;

		if (!selectedItem || !selectedRowNodes) {
			return;
		}

		const saveData = {
			taxonomy: selectedItem.data,
			selectedRowNodes: this.getSpendTypeTransactions(),
			createOverrideRule: shouldCreateRule,
		};

		return onSave(saveData);
	};

	getVendors = () => {
		const { selectedRowNodes, name } = this.props;

		if (selectedRowNodes) {
			const filterHelperMap = {};

			return this.getSpendTypeTransactions()
				.filter(rowNode => {
					const name = rowNode.data.HARMONIZED_VENDOR_NAME;

					if (filterHelperMap[name]) {
						return false;
					}

					filterHelperMap[name] = true;
					return true;
				})
				.map(rowNode => {
					return {
						harmonizedVendorId: rowNode.data.HARMONIZED_VENDOR_ID,
						harmonizedVendorName: rowNode.data.HARMONIZED_VENDOR_NAME,
						currentCategory: rowNode.data.AP_L4Category,
					};
				});
		}

		const { harmonizedVendors } = this.state;
		const vendorObj = harmonizedVendors.find(item => item.name === name);

		return [
			{
				harmonizedVendorName: name,
				harmonizedVendorId: (vendorObj && vendorObj.id) || null,
				currentCategory: '',
			},
		];
	};

	renderFooter = () => {
		const { onClose } = this.props;
		const {
			formStep,
			transactionsCount,
			selectedItem,
			isApplying,
		} = this.state;
		const isApplyDisabled =
			transactionsCount === 0 || !selectedItem || !selectedItem.data;

		if (isApplying) {
			return () => (
				<LoadingFooter>
					This window will close automatically when the change has been
					completed
				</LoadingFooter>
			);
		}

		if (formStep === OVERRIDE_STEP) {
			return () => (
				<ApButtonGroup>
					<ApButtonSecondary onClick={onClose}>Cancel</ApButtonSecondary>
					<ApButtonSecondary onClick={this.handleBackToEdit}>
						Edit Category
					</ApButtonSecondary>
					<ApButtonOrange onClick={this.handleApply}>Confirm</ApButtonOrange>
				</ApButtonGroup>
			);
		}

		return () => (
			<React.Fragment>
				<WarningMessage>
					<ApIcon iconName="outline_warning" iconColor={colors.amber} />
					Please note, you will not be able to roll back the change of category
					once you have clicked 'Apply'
				</WarningMessage>
				<ApButtonGroup>
					<ApButtonSecondary onClick={onClose}>Cancel</ApButtonSecondary>
					<ApButtonOrange onClick={this.handleApply} disabled={isApplyDisabled}>
						Apply
					</ApButtonOrange>
				</ApButtonGroup>
			</React.Fragment>
		);
	};

	renderClassificationOverrideContent() {
		const { selectedItem, isApplying } = this.state;
		const vendors = this.getVendors();

		if (isApplying) {
			return (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			);
		}

		return (
			<PreveiwContentWrapper>
				You are about to create an override rule for the following vendor(s).
				This will affect the categorisation of all future transactions for these
				vendor(s).
				<VendorsList>
					<VendorsListHeader>
						<VendorsListCell>Selected vendor(s)</VendorsListCell>
						<VendorsListCell>Current category</VendorsListCell>
						<VendorsListCell>New category</VendorsListCell>
					</VendorsListHeader>
					{vendors.map(vendor => (
						<VendorsListRow key={vendor.harmonizedVendorId}>
							<VendorsListCell>{vendor.harmonizedVendorName}</VendorsListCell>
							<VendorsListCell>{vendor.currentCategory}</VendorsListCell>
							<VendorsListCell>{selectedItem.value}</VendorsListCell>
						</VendorsListRow>
					))}
				</VendorsList>
			</PreveiwContentWrapper>
		);
	}

	render() {
		const { onClose, name, selectedRowNodes } = this.props;
		const {
			treeData,
			selectedItem,
			isLoading,
			treeQuery,
			isDirect,
			isAllTransactions,
			shouldCreateRule,
			transactionsCount,
			suppressSpendType,
			formStep,
			isApplying,
		} = this.state;
		const isIndirectClientTaxonomy =
			this.props.queryParams?.indirectTaxonomyType === 2 && !isDirect;
		const { spendType } = this.props;
		return (
			<StyledModal
				header={
					formStep === EDIT_STEP
						? `${name || 'Transaction'} categorisation`
						: 'Categorisation override'
				}
				footer={this.renderFooter()}
				hasClosed={onClose}
				isDismissible
				isOpen
				isApplying={isApplying}
			>
				{isLoading ? (
					<LoadingContainer>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoadingContainer>
				) : formStep === EDIT_STEP ? (
					<Content>
						<MainSection isApplying={isApplying}>
							<FormGroup>
								<OrangeText>
									{transactionsCount} spend item{transactionsCount > 1
										? 's'
										: ''}
									(transaction{transactionsCount > 1 ? 's' : ''}) selected
								</OrangeText>
							</FormGroup>
							<ActionableContent>
								{isApplying && (
									<LoadingWrapper>
										<ApLoaderDefault loaderText={() => <ApLoaderText />} />
									</LoadingWrapper>
								)}
								{!suppressSpendType && (
									<FormGroup>
										<Label>Apply change to (select single option):</Label>
										<HorizontalGroup>
											{(!isDirect || !spendType) && (
												<ApRadio
													name="isDirect"
													value={0}
													onChange={this.handleSpendTypeChange}
													checked={!isDirect}
												>
													<RadioLabel>Indirect</RadioLabel>
												</ApRadio>
											)}
											{!spendType && <span>or</span>}
											{(isDirect || !spendType) && (
												<ApRadio
													name="isDirect"
													value={1}
													onChange={this.handleSpendTypeChange}
													disabled={isIndirectClientTaxonomy}
													checked={isDirect}
												>
													<RadioLabel>Direct</RadioLabel>
												</ApRadio>
											)}
										</HorizontalGroup>
									</FormGroup>
								)}

								<FormGroup>
									<Text>
										{(this.props.queryParams?.indirectTaxonomyType === 2 ||
											this.props.queryParams?.indirectTaxonomyType === 1) &&
										isDirect
											? 'Use the taxonomy tree on the right-hand side to find and select the correct L4 category.'
											: isIndirectClientTaxonomy
												? 'Please select the appropriate L4 category from the taxonomy tree.'
												: 'Please search appropriate L4 category or find and select it in the taxonomy tree'}
									</Text>
									{(this.props.queryParams?.indirectTaxonomyType === 2 ||
										this.props.queryParams?.indirectTaxonomyType === 1) &&
									isDirect ? (
										<CategoryWrapper>
											{selectedItem && selectedItem.value ? (
												selectedItem.value
											) : (
												<PlaceholderText>
													{isIndirectClientTaxonomy
														? 'Select L4 category from taxonomy tree'
														: 'Selected L4 category...'}
												</PlaceholderText>
											)}
										</CategoryWrapper>
									) : (
										<SearchInput
											options={this.getSearchOptions()}
											onChange={this.selectSearchItem}
											selectedOption={selectedItem}
											placeholder={
												isIndirectClientTaxonomy
													? 'Select L4 category from taxonomy tree'
													: 'Search L4 category...'
											}
											isSelectable
										/>
									)}
								</FormGroup>
								{!selectedRowNodes && (
									<FormGroup>
										<Label>Change the status for:</Label>
										<RadioGroup>
											<ApRadio
												name="isAllTransactions"
												value={0}
												onChange={this.handleRadioChange}
												checked={!isAllTransactions}
											>
												<RadioLabelSmall>
													Only transactions in the selected view and applied
													filters
												</RadioLabelSmall>
											</ApRadio>
											<ApRadio
												name="isAllTransactions"
												value={1}
												onChange={this.handleRadioChange}
												checked={isAllTransactions}
											>
												<RadioLabelSmall>
													All transactions for this vendor
												</RadioLabelSmall>
											</ApRadio>
										</RadioGroup>
									</FormGroup>
								)}

								{!isDirect &&
									(!isIndirectClientTaxonomy && (
										<div>
											<Label>Future transactions</Label>
											<RadioGroup>
												<ApCheckbox
													type="checkbox"
													onChange={this.handleRuleChange}
													checked={shouldCreateRule}
													disabled={isIndirectClientTaxonomy}
												>
													<RadioLabelSmall>
														Create an override rule for future transactions from
														the selected vendor(s)
													</RadioLabelSmall>
												</ApCheckbox>
											</RadioGroup>
										</div>
									))}
							</ActionableContent>
						</MainSection>
						{!isApplying && (
							<RightSection>
								<SimpleTree
									onTreeItemClick={this.handleTreeItemClick}
									onChange={this.handleTreeDataChange}
									selectedTreeItem={selectedItem}
									searchQuery={treeQuery}
									data={treeData}
								/>
							</RightSection>
						)}
					</Content>
				) : (
					this.renderClassificationOverrideContent()
				)}
			</StyledModal>
		);
	}
}

export default React.memo(props => {
	const { currency, spendType } = useContext(QueryStringContext);
	const { permissions } = useContext(PermissionsContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);

	return (
		<CategorisationDialog
			{...props}
			permissions={permissions || []}
			currencySymbol={currencySymbol}
			currency={currency}
			spendType={spendType}
		/>
	);
});
